const ABSOLUTE_URL_REGEX = /^[a-zA-Z][a-zA-Z\d+\-.]*:\/\// // http(s), data, etc.
const PROTOCOL_RELATIVE_REGEX = /^\/\//
const TRAILING_SLASH_REGEX = /\/+$/

const DEFAULT_PROD_BASE = 'https://api.ksk.com.br/api'
const DEFAULT_DEV_PORT = import.meta.env.VITE_DEV_API_PORT
const DEFAULT_DEV_PATH = import.meta.env.VITE_DEV_API_PATH || ''

function normalizeBase(url = '') {
    if (!url) return ''
    return url.replace(TRAILING_SLASH_REGEX, '')
}

function extractProtocol(url = '') {
    if (!url) return null
    const match = url.match(/^([a-zA-Z][a-zA-Z\d+\-.]*):\/\//)
    return match ? match[1].toLowerCase() : null
}

function shouldForceRelative(base) {
    if (!base || !import.meta.env.DEV) return false
    if (typeof window === 'undefined') return false
    const pageProtocol = window.location.protocol.replace(':', '').toLowerCase()
    const baseProtocol = extractProtocol(base)
    if (!baseProtocol) return false
    return pageProtocol === 'https' && baseProtocol === 'http'
}

function buildDevFallback() {
    // If developer explicitly sets base, respect it
    if (import.meta.env.VITE_DEV_API_BASE_URL) {
        const explicit = normalizeBase(import.meta.env.VITE_DEV_API_BASE_URL)
        return shouldForceRelative(explicit) ? '' : explicit
    }

    const hasPortOrPath = DEFAULT_DEV_PORT || DEFAULT_DEV_PATH
    if (!hasPortOrPath) {
        // use Vite proxy (relative fetch) by default
        return ''
    }

    const host = import.meta.env.VITE_DEV_HOST || (typeof window !== 'undefined' ? window.location.hostname : 'localhost') || 'localhost'

    const preferHttps = import.meta.env.VITE_DEV_USE_HTTPS === 'true' || (typeof window !== 'undefined' ? window.location.protocol === 'https:' : false)
    const protocol = preferHttps ? 'https' : 'http'
    const port = DEFAULT_DEV_PORT ? `:${DEFAULT_DEV_PORT}` : ''
    const path = DEFAULT_DEV_PATH ? `/${DEFAULT_DEV_PATH.replace(/^\/+/, '')}` : ''
    const candidate = `${protocol}://${host}${port}${path}`.replace(TRAILING_SLASH_REGEX, '')
    return shouldForceRelative(candidate) ? '' : candidate
}

export function resolveApiBaseUrl() {
    const explicit = normalizeBase(import.meta.env.VITE_API_BASE_URL || '')
    if (explicit) {
        return shouldForceRelative(explicit) ? '' : explicit
    }
    if (import.meta.env.DEV) {
        return buildDevFallback()
    }
    return DEFAULT_PROD_BASE
}

export const API_BASE_URL = resolveApiBaseUrl()

function isAbsolute(input = '') {
    return ABSOLUTE_URL_REGEX.test(input) || PROTOCOL_RELATIVE_REGEX.test(input)
}

function mergeHeaders(defaults, current) {
    if (!defaults) return {...current}
    if (!current) return {...defaults}
    return {...defaults, ...current}
}

class ApiHttpClient {
    constructor({baseURL = '', headers = {}, credentials} = {}) {
        this.baseURL = normalizeBase(baseURL)
        this.defaultHeaders = headers
        this.credentials = credentials
    }

    withBase(path = '') {
        if (!this.baseURL || !path || isAbsolute(path)) {
            return path || this.baseURL || ''
        }
        return `${this.baseURL}${path.startsWith('/') ? path : `/${path}`}`
    }

    async request(path, init = {}) {
        const url = this.withBase(path)
        const headers = mergeHeaders(this.defaultHeaders, init.headers)
        const finalInit = {...init, headers}
        if (this.credentials && !finalInit.credentials) {
            finalInit.credentials = this.credentials
        }
        return fetch(url, finalInit)
    }

    get(path, init = {}) {
        return this.request(path, {...init, method: 'GET'})
    }

    post(path, body, init = {}) {
        return this.request(path, {...init, method: 'POST', body})
    }
}

export const apiHttpClient = new ApiHttpClient({
    baseURL: API_BASE_URL, headers: {'Accept': 'application/json'},
})

export function apiFetch(path, init) {
    return apiHttpClient.request(path, init)
}

export class BaseApiService {
    http() {
        return apiHttpClient
    }

    async getJson(responsePromise) {
        const res = await responsePromise
        if (!res?.ok) return Promise.reject(res)
        return res.json()
    }
}
